/**
 * Connection Sync Status Card Component
 *
 * Displays connection status for the Data page's "Connected Syncs" tab.
 * Shows dual-purpose visibility (inbound + outbound) per Inara's strategy.
 *
 * This is a STATUS DASHBOARD - NOT a full management interface.
 * Full OAuth/credential management is in Settings > Connections.
 *
 * @see /docs/ux-concepts/connections-cross-workflow-strategy.md
 */

import { Card, Space, Typography, Button, Tooltip, Divider, Switch } from 'antd'
import {
  CheckCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  SyncOutlined,
  PlayCircleOutlined,
  CloudDownloadOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons'
// useNavigate removed - not currently used but may be needed for future navigation
import type { Connection } from '@/types/connection.types'
import { computeDisplayState, getPlatformDisplayName } from '@/utils/connectionState'

const { Text } = Typography

/**
 * Health state for visual display
 */
export type SyncHealthState = 'healthy' | 'warning' | 'error' | 'disabled'

/**
 * Inbound sync configuration (data pulled INTO Rocket Pricing)
 */
export interface InboundSyncConfig {
  targetTable: string       // e.g., "Offerings", "Vendors"
  lastSyncAt?: string       // Human-readable: "5 min ago"
  schedule?: string         // e.g., "Every 15 minutes"
  autoPromote?: boolean     // Whether drafts are auto-promoted
}

/**
 * Outbound sync configuration (data pushed OUT to platform)
 */
export interface OutboundSyncConfig {
  configured: boolean
  destination?: string      // e.g., "Price Lists", "Product Catalog"
  lastExportAt?: string     // Human-readable: "1 day ago"
  schedule?: string
}

/**
 * Props for ConnectionSyncStatusCard
 */
export interface ConnectionSyncStatusCardProps {
  /** Connection entity */
  connection: Connection

  /** Inbound sync configuration (Data step) */
  inbound?: InboundSyncConfig

  /** Outbound sync configuration (Exports step) */
  outbound?: OutboundSyncConfig

  /** Callback when "Sync Now" is clicked */
  onSyncNow?: () => void

  /** Callback when "Review Drafts" is clicked */
  onReviewDrafts?: () => void

  /** Callback when "View Logs" is clicked */
  onViewLogs?: () => void

  /** Callback when auto-activate toggle is changed */
  onToggleAutoActivate?: (value: boolean) => void
}

/**
 * Compute sync-level health from connection status and sync config
 */
function computeSyncHealth(
  connection: Connection,
  _inbound?: InboundSyncConfig
): SyncHealthState {
  const displayState = computeDisplayState(connection)

  // Map display colors to sync health
  if (displayState.color === 'green') return 'healthy'
  if (displayState.color === 'red') return 'error'
  if (displayState.color === 'yellow' || displayState.color === 'orange') return 'warning'
  if (displayState.color === 'gray') return 'disabled'

  // Default based on operational status
  if (connection.credentialStatus === 'valid' && connection.state === 'active') {
    return 'healthy'
  }

  return 'warning'
}

/**
 * Health indicator component
 */
function HealthIndicator({ health }: { health: SyncHealthState }) {
  const config = {
    healthy: {
      icon: <CheckCircleOutlined />,
      color: 'var(--rp-success)',
      label: 'Healthy',
    },
    warning: {
      icon: <WarningOutlined />,
      color: 'var(--rp-temporal-orange)',
      label: 'Warning',
    },
    error: {
      icon: <CloseCircleOutlined />,
      color: 'var(--rp-temporal-red)',
      label: 'Error',
    },
    disabled: {
      icon: <SyncOutlined />,
      color: 'var(--rp-border-subtle)',
      label: 'Disabled',
    },
  }

  const { icon, color, label } = config[health]

  return (
    <Space size={4}>
      <span style={{ color, fontSize: '16px' }}>{icon}</span>
      <Text style={{ color }}>{label}</Text>
    </Space>
  )
}

/**
 * Connection Sync Status Card
 *
 * Displays connection status with dual-purpose visibility (inbound + outbound).
 * Designed as a READ-ONLY status dashboard with quick actions.
 *
 * Per Inara's strategy:
 * - Shows BOTH inbound (Data) AND outbound (Exports) status in one card
 * - [Manage Connection] navigates to Settings > Connections
 * - Quick actions (Sync Now, View Logs) available without leaving page
 *
 * @example
 * ```tsx
 * <ConnectionSyncStatusCard
 *   connection={connection}
 *   inbound={{
 *     targetTable: 'Offerings',
 *     lastSyncAt: '5 min ago',
 *   }}
 *   onSyncNow={() => triggerSync(connection.uid)}
 * />
 * ```
 */
export function ConnectionSyncStatusCard({
  connection,
  inbound,
  outbound: _outbound, // Reserved for future outbound sync display
  onSyncNow,
  onReviewDrafts: _onReviewDrafts, // Reserved for future "Review Drafts" button
  onViewLogs: _onViewLogs, // Reserved for future "View Logs" button
  onToggleAutoActivate,
}: ConnectionSyncStatusCardProps) {
  const health = computeSyncHealth(connection, inbound)
  const platformName = getPlatformDisplayName(connection.platform)
  const displayState = computeDisplayState(connection)

  return (
    <Card
      style={{
        borderLeft: `4px solid ${
          health === 'healthy' ? 'var(--rp-success)' :
          health === 'warning' ? 'var(--rp-temporal-orange)' :
          health === 'error' ? 'var(--rp-temporal-red)' :
          'var(--rp-border-subtle)'
        }`,
      }}
    >
      {/* Header: Platform name + Health indicator */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <Space>
          <Text strong style={{ fontSize: '16px' }}>{platformName}</Text>
          {connection.metadata?.lastTestResult?.accountSummary?.accountName && (
            <Text type="secondary">
              ({connection.metadata.lastTestResult.accountSummary.accountName})
            </Text>
          )}
        </Space>
        <HealthIndicator health={health} />
      </div>

      <Divider style={{ margin: '12px 0' }} />

      {/* Inbound Section */}
      <div style={{ marginBottom: '16px' }}>
        <Space style={{ marginBottom: '8px' }}>
          <CloudDownloadOutlined style={{ color: '#1890ff' }} />
          <Text strong>Inbound</Text>
          <Text type="secondary">(Data Step)</Text>
        </Space>

        {inbound ? (
          <div style={{ paddingLeft: '22px' }}>
            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              {/* Last sync info */}
              {inbound.lastSyncAt && (
                <Text type="secondary">Last sync: {inbound.lastSyncAt}</Text>
              )}

              {/* Auto-activate toggle */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Switch
                  checked={inbound.autoPromote ?? false}
                  onChange={onToggleAutoActivate}
                  size="small"
                />
                <Text>Auto-activate imports</Text>
                <Tooltip title="When enabled, synced items are automatically activated. When disabled, they remain as drafts for manual review.">
                  <QuestionCircleOutlined style={{ color: '#8c8c8c', cursor: 'help' }} />
                </Tooltip>
              </div>
            </Space>
          </div>
        ) : (
          <div style={{ paddingLeft: '22px' }}>
            <Text type="secondary">(Not configured)</Text>
          </div>
        )}
      </div>

      <Divider style={{ margin: '12px 0' }} />

      {/* Quick Actions */}
      <Space>
        {onSyncNow && (
          <Tooltip title="Trigger immediate sync">
            <Button
              type="primary"
              icon={<PlayCircleOutlined />}
              onClick={onSyncNow}
              disabled={health === 'error' || health === 'disabled'}
            >
              Sync Now
            </Button>
          </Tooltip>
        )}
      </Space>

      {/* Warning message for error states */}
      {(health === 'error' || health === 'warning') && (
        <div style={{ marginTop: '12px', padding: '8px 12px', background: health === 'error' ? '#fff2f0' : '#fffbe6', borderRadius: '4px' }}>
          <Text type={health === 'error' ? 'danger' : 'warning'}>
            {displayState.description}
          </Text>
        </div>
      )}
    </Card>
  )
}

export default ConnectionSyncStatusCard
